import { supabase } from "../auth/supabaseClient";
import { readImpersonate } from "../auth/impersonateStore";
import { buildApiHeaders } from "./headers";

export { buildApiHeaders } from "./headers";

// ---------------------------------------------------------------------------
// API-Client — der einzige Ort, an dem Auth-Header gebaut werden.
// ---------------------------------------------------------------------------
// Jede App-Komponente ruft apiGet/apiPost. Tokens kommen aus der aktuellen
// Supabase-Session, Impersonation aus sessionStorage.

export interface ApiErrorBody {
  ok: false;
  error: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function buildHeaders(init?: HeadersInit): Promise<Headers> {
  const { data } = await supabase.auth.getSession();
  return buildApiHeaders(data.session?.access_token ?? null, readImpersonate(), init);
}

async function parse<T>(res: Response): Promise<T> {
  // Vercel sendet bei unhandled exceptions oder Function-Timeouts plain-text
  // ("An error occurred …") ohne JSON-Content-Type. Das wuerde res.json()
  // mit "Unexpected token 'A'" kaputt machen — darum erst Text lesen, dann
  // versuchsweise als JSON parsen.
  const rawText = await res.text();
  let body: T | ApiErrorBody | null = null;
  let jsonParseError: string | null = null;
  if (rawText.length > 0) {
    try {
      body = JSON.parse(rawText) as T | ApiErrorBody;
    } catch (err) {
      jsonParseError = err instanceof Error ? err.message : String(err);
    }
  }

  if (!res.ok) {
    if (body && typeof body === "object" && "error" in body) {
      const err = body as ApiErrorBody;
      throw new ApiError(res.status, err.error, err.code);
    }
    // Kein JSON-Body vom Server (Vercel-Default-Error o.ae.). Wir geben den
    // Raw-Text als Message — sonst sieht der User nur "Unexpected token 'A'".
    const snippet = rawText.slice(0, 500).trim();
    const msg = snippet.length > 0
      ? `HTTP ${String(res.status)}: ${snippet}`
      : `HTTP ${String(res.status)} (leerer Body)`;
    throw new ApiError(res.status, msg, "NON_JSON_RESPONSE");
  }

  if (body === null) {
    throw new ApiError(
      res.status,
      `Server-Antwort ist kein JSON: ${jsonParseError ?? "leerer Body"}`,
      "INVALID_JSON_RESPONSE",
    );
  }
  return body as T;
}

// VTR-8 (24.08.2026): WESSEN Inhalt eine Antwort traegt, entscheiden bei
// diesen Abrufen die Kopfzeilen (Authorization, X-Act-As-User-Id) — der
// Browser-HTTP-Cache schluesselt aber NUR nach URL. Ein `max-age` auf
// /api/notifications reichte, damit nach einem Vertretungs-Wechsel Daniels
// eigene Antwort unter dem Namen des Vertretenen stand, ohne dass je ein
// Request rausging. `no-store` an DIESER einen Stelle entschaerft die ganze
// Klasse: welcher Endpoint auch immer kuenftig einen Cache-Header setzt,
// dieser Client nimmt nie eine URL-genau gespeicherte Antwort an.
// Gilt fuer apiGet und apiGetText; apiGetBlob bleibt bewusst draussen —
// siehe den Kommentar dort.
const IDENTITY_DEPENDENT_GET: RequestCache = "no-store";

export async function apiGet<T>(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<T> {
  const init: RequestInit = {
    method: "GET",
    headers: await buildHeaders(),
    cache: IDENTITY_DEPENDENT_GET,
  };
  if (options.signal) init.signal = options.signal;
  const res = await fetch(path, init);
  return parse<T>(res);
}

// GET, der den Response-Body als raw text zurueckliefert (z.B. fuer
// text/html-Endpoints wie `/api/offers/html`, das im iframe srcDoc
// landet). Fehler-Antworten werden weiterhin als `ApiError` mit JSON-error-
// Mapping geworfen, damit Modal-Layer dieselben Code-Branches kennt.
export async function apiGetText(path: string): Promise<string> {
  const res = await fetch(path, {
    method: "GET",
    headers: await buildHeaders(),
    cache: IDENTITY_DEPENDENT_GET,
  });
  const rawText = await res.text();
  if (!res.ok) {
    // Server-Side errors aus `withAuth`/`withRole`/Handler liefern JSON —
    // wir versuchen optimistisch zu parsen, sonst Raw-Text-Snippet als
    // Message.
    try {
      const body = JSON.parse(rawText) as ApiErrorBody;
      if (body && typeof body === "object" && "error" in body) {
        throw new ApiError(res.status, body.error, body.code);
      }
    } catch (err) {
      if (err instanceof ApiError) throw err;
    }
    const snippet = rawText.slice(0, 500).trim();
    const msg = snippet.length > 0
      ? `HTTP ${String(res.status)}: ${snippet}`
      : `HTTP ${String(res.status)} (leerer Body)`;
    throw new ApiError(res.status, msg, "NON_JSON_RESPONSE");
  }
  return rawText;
}

// FOTO-1: GET, der den Body als Blob zurueckliefert (Bilder). Ein blosses
// `<img src="/api/…">` geht hier NICHT: die API verlangt den Bearer-Token im
// Kopf, und ein img-Element schickt keine Kopfzeilen mit — es faenge sich ein
// 401. Darum holen wir die Bytes wie jeden anderen Aufruf und machen daraus
// eine Object-URL.
//
// VTR-8: hier BEWUSST kein `no-store`. Invoice-PDF und Offer-Bild senden
// `private, max-age=300`, und das ist dort richtig: ihr Inhalt haengt an der
// Entity-Id in der URL, nicht am Anfragenden — dieselbe URL ist fuer jeden
// dieselben Bytes, die Identitaet entscheidet nur ueber den ZUGANG (403),
// und Fehler-Antworten cached der Browser nicht. `no-store` nachziehen
// wuerde die PDF-Wiederverwendung wegnehmen und nichts absichern.
export async function apiGetBlob(path: string): Promise<Blob> {
  const res = await fetch(path, {
    method: "GET",
    headers: await buildHeaders(),
  });
  if (!res.ok) {
    let msg = `HTTP ${String(res.status)}`;
    let code = "NON_JSON_RESPONSE";
    try {
      const body = (await res.json()) as ApiErrorBody;
      if (body && typeof body === "object" && "error" in body) {
        msg = body.error;
        code = body.code ?? code;
      }
    } catch {
      // Kein JSON — die Statuszeile muss reichen.
    }
    throw new ApiError(res.status, msg, code);
  }
  return await res.blob();
}

// KUND-1 (11.08.2026): hier lief `withProposalCheck` — jede schreibende
// Antwort wurde auf ein `customerMail`-Feld abgeklopft und liess damit den
// Freigabe-Dialog aufspringen. Mit dem Wegfall des automatischen Fensters gibt
// es keinen Erzeuger mehr; die Durchreiche ist mit weg, statt tot zu bleiben.

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: await buildHeaders(),
    body: JSON.stringify(body),
  });
  return await parse<T>(res);
}

/**
 * ENG-11: POST, der bei !ok NICHT wirft, sondern Status + geparsten Body
 * zurueckgibt. Fuer Endpoints, deren Fehler-Body Nutzlast traegt (z.B. die
 * bexio-Rechnungs-Pipeline: `stages` + `code` entscheiden ueber den Retry-
 * Einstieg) — `ApiError` transportiert nur status/message/code.
 */
export async function apiPostDetailed<T>(
  path: string,
  body: unknown,
): Promise<{ status: number; body: T | null }> {
  const res = await fetch(path, {
    method: "POST",
    headers: await buildHeaders(),
    body: JSON.stringify(body),
  });
  const rawText = await res.text();
  let parsed: T | null = null;
  if (rawText.length > 0) {
    try {
      parsed = JSON.parse(rawText) as T;
    } catch {
      parsed = null;
    }
  }
  return { status: res.status, body: parsed };
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PATCH",
    headers: await buildHeaders(),
    body: JSON.stringify(body),
  });
  return await parse<T>(res);
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    headers: await buildHeaders(),
    body: JSON.stringify(body),
  });
  return await parse<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: "DELETE",
    headers: await buildHeaders(),
  });
  return parse<T>(res);
}
